import { useCallback, useEffect, useState } from 'react';

import { monitorApi } from '../api/monitor';
import type {
  AccountStatusFilter,
  Master,
  MasterChanges,
  NewMaster,
  OperatorOption,
} from '../api/monitor';

export interface MastersController {
  masters: Master[];
  /** As operadoras cadastradas — uma linha de ciclo para cada no formulário. */
  operators: OperatorOption[];
  /** Situação filtrada; a tela abre nas ativas. */
  status: AccountStatusFilter;
  loading: boolean;
  error: string | null;
  saving: boolean;
  setStatus: (status: AccountStatusFilter) => void;
  create: (payload: NewMaster) => Promise<Master | null>;
  /** Edita e recarrega a lista — inativar pode tirar a master do filtro. */
  update: (id: number, changes: MasterChanges) => Promise<Master | null>;
  reload: () => void;
}

export function useMasters(): MastersController {
  const [masters, setMasters] = useState<Master[]>([]);
  const [operators, setOperators] = useState<OperatorOption[]>([]);
  const [status, setStatus] = useState<AccountStatusFilter>('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    monitorApi
      .operators()
      .then((response) => setOperators(response.operators))
      .catch(() => setOperators([]));
  }, []);

  useEffect(() => {
    let current = true;
    setLoading(true);

    monitorApi
      .masters(status)
      .then((response) => {
        if (!current) return;
        setMasters(response.masters);
        setError(null);
      })
      .catch((cause: Error) => {
        if (current) setError(cause.message);
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    return () => {
      current = false;
    };
  }, [status, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  const create = useCallback(async (payload: NewMaster) => {
    setSaving(true);
    try {
      const response = await monitorApi.createMaster(payload);
      setReloadToken((token) => token + 1);
      return response.master;
    } finally {
      setSaving(false);
    }
  }, []);

  const update = useCallback(async (id: number, changes: MasterChanges) => {
    setSaving(true);
    try {
      const response = await monitorApi.updateMaster(id, changes);
      setReloadToken((token) => token + 1);
      return response.master;
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    masters,
    operators,
    status,
    loading,
    error,
    saving,
    setStatus,
    create,
    update,
    reload,
  };
}
